import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { Einrichtung, EinrichtungPatch, EinrichtungWrite, UUID } from './types';

export function useEinrichtungList(traegerId: UUID | null) {
  return useQuery({
    queryKey: queryKeys.einrichtung.list(traegerId),
    queryFn: async (): Promise<Einrichtung[]> => {
      if (!traegerId) return [];
      const res = await apiClient.get<Einrichtung[]>('/einrichtung', {
        params: { traeger_id: traegerId },
      });
      return res.data;
    },
    enabled: !!traegerId,
    staleTime: 60_000,
  });
}

export function useEinrichtung(id: UUID | null) {
  return useQuery({
    queryKey: id ? queryKeys.einrichtung.detail(id) : ['einrichtung', 'detail', 'none'],
    queryFn: async (): Promise<Einrichtung> => {
      if (!id) throw new Error('id required');
      const res = await apiClient.get<Einrichtung>(`/einrichtung/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateEinrichtung() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EinrichtungWrite): Promise<Einrichtung> => {
      const res = await apiClient.post<Einrichtung>('/einrichtung', body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einrichtung.all });
    },
  });
}

export function useUpdateEinrichtung() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID; patch: EinrichtungPatch }): Promise<Einrichtung> => {
      const res = await apiClient.patch<Einrichtung>(`/einrichtung/${vars.id}`, vars.patch);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einrichtung.all });
    },
  });
}

export function useDeleteEinrichtung() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/einrichtung/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.einrichtung.all });
    },
  });
}
